import { useForm, Controller } from 'react-hook-form';
import { zodResolver } from '@hookform/resolvers/zod';
import { useLocation } from 'react-router-dom';
import { useTranslation } from 'react-i18next';
import { motion } from 'framer-motion';
import { LogIn, User } from 'lucide-react';
import { Button } from '@core/components/ui/shared/buttons/button';
import { Label } from '@core/components/ui/shared/labels/label';
import { FloatingInput } from '@core/components/ui/shared/inputs/floating-input';
import { PasswordInput } from '@/components/password-input';
import { Checkbox } from '@core/components/ui/shared/checkboxes/checkbox';
import { loginSchema, type LoginFormData } from '@/validations/schemas';
import useBackendLogin from '@/hooks/use-backend-login';
import { containerVariants, itemVariants } from '@/lib/animations';

interface Props {
  onForgotPassword: () => void;
}

const LoginForm = ({ onForgotPassword }: Props) => {
  const { t } = useTranslation();
  const location = useLocation();
  const params = new URLSearchParams(location.search);
  const returnUrl = params.get('returnUrl') ?? '/';
  const { login, isLoading } = useBackendLogin(returnUrl);

  const form = useForm<LoginFormData>({
    resolver: zodResolver(loginSchema),
    defaultValues: {
      login: '',
      password: '',
      rememberMe: false,
    },
  });

  const onSubmit = async (data: LoginFormData): Promise<void> => {
    await login({
      username: data.login,
      password: data.password,
      rememberMe: data.rememberMe,
    });
  }

  return (
    <motion.div
      className="space-y-6 sm:space-y-8"
      variants={containerVariants}
      initial="hidden"
      animate="visible"
    >
      <motion.div variants={itemVariants} className="text-center space-y-2">
        <motion.div
          className="flex justify-center mb-4"
          initial={{ scale: 0 }}
          animate={{ scale: 1 }}
          transition={{ type: 'spring', stiffness: 200, damping: 15 }}
        >
          <div className="p-3 sm:p-4 rounded-full bg-primary/10">
            <User className="h-8 w-8 sm:h-10 sm:w-10 text-primary" />
          </div>
        </motion.div>
        <h1 className="text-2xl font-bold sm:text-3xl md:text-4xl">{t('signIn')}</h1>
        <p className="text-sm text-muted-foreground sm:text-base">
          {t('signInSubtitle')}
        </p>
      </motion.div>

      <form onSubmit={form.handleSubmit(onSubmit)} className="space-y-4 sm:space-y-5">
        <motion.div variants={itemVariants} className="space-y-1">
          <Controller
            name="login"
            control={form.control}
            render={({ field }) => (
              <FloatingInput
                id="login"
                label={t('login')}
                className="h-12 sm:h-14 text-base sm:text-lg"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={!!form.formState.errors.login}
                readOnly={isLoading}
              />
            )}
          />
          <div className="min-h-5">
            {form.formState.errors.login && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs sm:text-sm text-destructive"
              >
                {t(form.formState.errors.login.message!)}
              </motion.p>
            )}
          </div>
        </motion.div>

        <motion.div variants={itemVariants} className="space-y-1">
          <Controller
            name="password"
            control={form.control}
            render={({ field }) => (
              <PasswordInput
                id="password"
                label={t('password')}
                className="h-12 sm:h-14 text-base sm:text-lg"
                value={field.value}
                onChange={field.onChange}
                onBlur={field.onBlur}
                aria-invalid={!!form.formState.errors.password}
                readOnly={isLoading}
              />
            )}
          />
          <div className="min-h-5">
            {form.formState.errors.password && (
              <motion.p
                initial={{ opacity: 0 }}
                animate={{ opacity: 1 }}
                className="text-xs sm:text-sm text-destructive"
              >
                {t(form.formState.errors.password.message!)}
              </motion.p>
            )}
          </div>
        </motion.div>

        <motion.div
          variants={itemVariants}
          className="flex flex-col gap-4 sm:flex-row sm:items-center sm:justify-between"
        >
          <Controller
            name="rememberMe"
            control={form.control}
            render={({ field }) => (
              <div className="flex items-center gap-2">
                <Checkbox
                  id="rememberMe"
                  checked={field.value}
                  onCheckedChange={field.onChange}
                  className="size-5"
                />
                <Label
                  htmlFor="rememberMe"
                  className="text-sm sm:text-base text-muted-foreground cursor-pointer select-none"
                >
                  {t('rememberMe')}
                </Label>
              </div>
            )}
          />

          <motion.button
            type="button"
            onClick={onForgotPassword}
            className="text-sm sm:text-base text-primary hover:text-primary/80 font-medium transition-colors"
            whileHover={{ x: 3 }}
            whileTap={{ scale: 0.98 }}
          >
            {t('forgotPassword')}
          </motion.button>
        </motion.div>

        <motion.div variants={itemVariants} className="pt-2 sm:pt-4">
          <Button
            type="submit"
            size="lg"
            className="w-full h-12 sm:h-14 text-base sm:text-lg font-semibold group"
            disabled={isLoading}
          >
            {isLoading ? (
              <motion.div
                className="h-5 w-5 border-2 border-current border-t-transparent rounded-full"
                animate={{ rotate: 360 }}
                transition={{ duration: 1, repeat: Infinity, ease: 'linear' }}
              />
            ) : (
              <>
                <LogIn className="mr-2 h-5 w-5 transition-transform group-hover:translate-x-1" />
                {t('signIn')}
              </>
            )}
          </Button>
        </motion.div>
      </form>
    </motion.div>
  );
}

export default LoginForm;